import { Action, CanvasWidget, InputType } from "@projectstorm/react-canvas-core";
import createEngine, { DiagramModel } from "@projectstorm/react-diagrams";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  automationSessionData,
  automationTreeViewItems,
  engine,
  selectedAutomationNode,
  selectedAutomationTreeViewItem,
} from "../NewTemplateSignals";
import { AtlasCanvasWidget } from "./AtlasCanvasWidget";

class CustomDeleteItemsAction extends Action {
  constructor(options = {}) {
    const { keyCodes = [46, 8], canvasRef } = options;
    super({
      type: InputType.KEY_DOWN,
      fire: (event) => {
        if (keyCodes.indexOf(event.event.keyCode) !== -1) {
          if (canvasRef?.current?.contains(document.activeElement)) {
            // Check if the active element is an input or contentEditable
            const activeElement = document.activeElement;
            if (
              activeElement.tagName === "INPUT" ||
              activeElement.tagName === "TEXTAREA" ||
              activeElement.isContentEditable
            ) {
              // Allow default behavior for input elements
              return;
            }

            event.event.preventDefault();
            console.log("Delete/Backspace action triggered in diagram");

            const selectedEntities = this.engine.getModel().getSelectedEntities();
            if (selectedEntities.length > 0) {
              const confirm = window.confirm("Are you sure you want to delete?");
              if (confirm) {
                selectedEntities.forEach((model) => {
                  if (!model.isLocked()) {
                    model.remove();
                  }
                });
                this.engine.repaintCanvas();
              }
            }
          }
        }
      },
    });
    this.canvasRef = canvasRef;
  }
}

export const AtlasDiagramWidget = () => {
  const [localEngine, setLocalEngine] = useState(null);
  const canvasRef = useRef(null);

  const handleKeyDown = useCallback((event) => {
    if (
      (event.key === "Backspace" || event.key === "Delete") &&
      canvasRef?.current?.contains(event.target)
    ) {
      const activeElement = document.activeElement;
      if (
        activeElement.tagName !== "INPUT" &&
        activeElement.tagName !== "TEXTAREA" &&
        !activeElement.isContentEditable
      ) {
        event.preventDefault();
        console.log("Key pressed:", event.key);
      }
    }
  }, []);

  useEffect(() => {
    const newEngine = createEngine({ registerDefaultDeleteItemsAction: false });
    newEngine.setMaxNumberPointsPerLink(0);
    newEngine.setModel(new DiagramModel());

    newEngine.getActionEventBus().registerAction(new CustomDeleteItemsAction({ canvasRef }));
    setLocalEngine(newEngine);
    engine.value = newEngine;

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!localEngine) return;

    if (selectedAutomationTreeViewItem.value) {
      const selectedAutomation = automationSessionData.value.find(
        (automation) => automation.id === selectedAutomationTreeViewItem.value
      );

      if (selectedAutomation?.model) {
        const newModel = selectedAutomation.model;

        newModel.getNodes().forEach((node) => {
          node.registerListener({
            selectionChanged: (event) => {
              const newModel = automationSessionData.value.find(
                (automation) => automation.id === selectedAutomationTreeViewItem.value
              ).model;
              node.selected = event.isSelected;
              automationSessionData.value = automationSessionData.value.map((automation) => {
                if (automation.id === selectedAutomationTreeViewItem.value) {
                  return {
                    ...automation,
                    model: newModel,
                  };
                }
                return automation;
              });
            },
          });
        });

        // Set up node selection and link creation listeners
        newModel.registerListener({
          selectionChanged: (event) => {
            const selectedNodes = event.isSelected;
            if (selectedNodes.length > 0) {
              const selectedNodeId = selectedNodes[0].getOptions().id;
              selectedAutomationNode.value = selectedNodeId;
            } else {
              selectedAutomationNode.value = null;
            }
          },
        });

        localEngine.setModel(newModel);
        localEngine.repaintCanvas();
      }
    }
  }, [
    localEngine,
    selectedAutomationTreeViewItem.value,
    automationSessionData.value,
    automationTreeViewItems.value,
  ]);

  if (!localEngine || !selectedAutomationTreeViewItem.value) {
    return (
      <AtlasCanvasWidget
        background="rgb(60, 60, 60)"
        color="rgba(255,255,255, 0.05)"
        vignetteOpacity={0.5}
      />
    );
  }

  return (
    <div
      ref={canvasRef}
      style={{ height: "100%", width: "100%" }}
      tabIndex={0}
    >
      <AtlasCanvasWidget
        background="rgb(60, 60, 60)"
        color="rgba(255,255,255, 0.05)"
        vignetteOpacity={0.5}
      >
        <CanvasWidget engine={localEngine} />
      </AtlasCanvasWidget>
    </div>
  );
};
